import { mapGenreToMetaFilters } from "../config/genreTagMapping";
import { TAG_EXPANSIONS } from "../constants/categories";
import { Station, StationSource, HomeRow, SupabaseStationRow } from "../types";

import type { FilterRules } from "./carouselQueryService";
import operators from "./data/operators.json";
import {
  SWEDISH_STATIONS,
  NRK_STATIONS,
  RTP_STATIONS,
} from "./data/staticStations";
import {
  getRbStations,
  getRbStationsByGeo,
  getRbStationsForStats,
  getRbCountries,
  getRbStationById,
} from "./providers/radioBrowserProvider";
import { getSverigesRadioStations } from "./providers/sverigesRadioProvider";
import { supabase } from "./supabase";
import {
  getUserHomePreferences,
  UserHomePreference,
} from "./userLibraryService";

const CACHE_KEY_PREFIX = "v11_home_data_";
const CACHE_DURATION = 1000 * 60 * 60 * 24; // 24 hours

const ALL_STATIC_STATIONS = [
  ...SWEDISH_STATIONS,
  ...NRK_STATIONS,
  ...RTP_STATIONS,
];

// Countries to exclude from all station results
// Allows: Western countries, Thailand, Japan, South Korea, Taiwan
const BLOCKED_COUNTRIES = new Set([
  "russia",
  "russian federation",
  "china",
  "iran",
  "north korea",
  "syria",
  "belarus",
  "cuba",
  "venezuela",
  "nicaragua",
  "eritrea",
  "turkmenistan",
  "afghanistan",
  "iraq",
  "yemen",
  "libya",
  "sudan",
  "somalia",
  "egypt",
  "saudi arabia",
  "kuwait",
  "united arab emirates",
  "qatar",
  "bahrain",
  "oman",
  "jordan",
  "lebanon",
  "algeria",
  "morocco",
  "tunisia",
  "palestine",
  "hungary",
  "israel", // Added
  // Country codes
  "ru",
  "cn",
  "ir",
  "kp",
  "sy",
  "by",
  "cu",
  "ve",
  "ni",
  "er",
  "tm",
  "af",
  "iq",
  "ye",
  "ly",
  "sd",
  "so",
  "eg",
  "sa",
  "kw",
  "ae",
  "qa",
  "bh",
  "om",
  "jo",
  "lb",
  "dz",
  "ma",
  "tn",
  "ps",
  "hu",
  "il", // Israel code
]);

// Block specific broken stations
const BLOCKED_STATIONS = new Set([
  "adroit jazz underground", // Broken stream reported by user
  "adroit jazz",
]);

// Block stream URL patterns from sensitive countries (catches stations with incorrect metadata)
const BLOCKED_URL_PATTERNS = [
  ".ru/",
  ".ru:",
  "//ru.", // Russian domains
  ".cn/",
  ".cn:",
  "//cn.", // Chinese domains
  ".ir/",
  ".ir:",
  "//ir.", // Iranian domains
  ".by/",
  ".by:",
  "//by.", // Belarusian domains
  ".kp/",
  ".kp:",
  "//kp.", // North Korean domains
  ".sy/",
  ".sy:",
  "//sy.", // Syrian domains
  "europaplus",
  "europeplus",
  "europa-plus",
  "europe-plus", // Russian radio brand
  "emg.fm", // European Media Group (Russian)
  "nashe.ru",
  "rusradio",
  "radiovesti",
  "radiorossii", // Other Russian radios
];

// Block station name patterns (for stations that slip through with wrong metadata)
const BLOCKED_NAME_PATTERNS = [
  "europa plus",
  "europaplus",
  "europe plus",
  "europeplus",
  "radio rossii",
  "radio russia",
  "russian radio",
  "голос",
  "радио",
  "россия", // Cyrillic patterns
  "中国",
  "电台", // Chinese patterns
  // Religious content
  "christian",
  "gospel",
  "worship",
  "praise",
  "bible",
  "jesus",
  "christ",
  "catholic",
  "protestant",
  "evangelical",
  "baptist",
  "lutheran",
  "methodist",
  "pentecostal",
  "adventist",
  "mormon",
  "jehovah",
  "islamic",
  "muslim",
  "quran",
  "mosque",
  "allah",
  "muhammad",
  "hindu",
  "krishna",
  "temple",
  "mandir",
  "buddhist",
  "dharma",
  "sangha",
  "jewish",
  "torah",
  "synagogue",
  "kosher",
  "religious",
  "faith",
  "prayer",
  "sermon",
  "church radio",
  "god radio",
];

// Blocked tags (for filtering by station tags)
const BLOCKED_TAGS = new Set([
  "christian",
  "gospel",
  "religious",
  "religion",
  "worship",
  "praise",
  "catholic",
  "protestant",
  "evangelical",
  "baptist",
  "lutheran",
  "islamic",
  "muslim",
  "hindu",
  "buddhist",
  "jewish",
  "faith",
  "prayer",
  "church",
  "bible",
  "quran",
  "torah",
  "spiritual",
]);

// Helper function to check if a station should be blocked
const isBlockedCountry = (station: Station): boolean => {
  const country = (station.country || "").toLowerCase();
  const countryCode = (station.countryCode || "").toLowerCase();
  const name = (station.name || "").toLowerCase();
  const streamUrl = (station.streamUrl || "").toLowerCase();
  const website = (station.website || "").toLowerCase();
  const tags = (station.tags || []).map((t) => t.toLowerCase());

  // Check countries
  if (BLOCKED_COUNTRIES.has(country) || BLOCKED_COUNTRIES.has(countryCode))
    return true;

  // Check broken stations
  if (BLOCKED_STATIONS.has(name)) return true;

  // Check stream URL patterns
  if (
    BLOCKED_URL_PATTERNS.some(
      (pattern) => streamUrl.includes(pattern) || website.includes(pattern),
    )
  )
    return true;

  // Check station name patterns
  if (BLOCKED_NAME_PATTERNS.some((pattern) => name.includes(pattern)))
    return true;

  // Check station tags
  if (tags.some((tag) => BLOCKED_TAGS.has(tag))) return true;

  return false;
};

// --- Helpers ---

const mapSupabaseStation = (s: SupabaseStationRow): Station => ({
  id: s.id,
  name: s.name,
  country: s.country,
  countryCode: s.country_code,
  state: s.state,
  provider: s.provider as StationSource,
  streamUrl: s.stream_url,
  website: s.website,
  logoUrl: s.logo_url,
  bitrate: s.bitrate,
  codec: s.codec,
  language: s.language && typeof s.language === 'string' ? s.language.split(",").map((l) => l.trim()) : Array.isArray(s.language) ? s.language : [],
  tags: s.tags || [],
  votes: s.votes,
  clickTrend: s.click_trend,
  onlineStatus: s.online_status ? "online" : "offline",
  stationType:
    s.provider === "sr" ||
    s.provider === "nrk" ||
    s.provider === "yle" ||
    s.provider === "bbc"
      ? "mainstream"
      : "online",
});

/**
 * Enriches a station object with improved metadata, logos, and operator info.
 * Tries to match against static lists (e.g., SR, NRK) to provide verified logos and colors.
 *
 * @param station The raw station object from a provider
 * @returns The enriched station object
 */
export const enrichStationData = (station: Station): Station => {
  // 0. Clean Name (Global Polish)
  // Remove leading/trailing formatting characters like hyphens, bullets, dots
  // Example: "- 0 N -" -> "0 N" | "My Station -" -> "My Station"
  if (station.name) {
    station.name = station.name
      .replace(/^[\s\-.,|•]+/, "") // Remove leading separators
      .replace(/[\s\-.,|•]+$/, "") // Remove trailing separators
      .trim();
  }

  // 1. Try to match with a curated static station (Highest Priority for Logos)
  // We match by ID first, then by flexible name matching

  // Helper to normalize station names for comparison (aggressive normalization)
  const normalizeName = (name: string) =>
    name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ")
      .replace(/[0-9.,]+\s*(fm|am|mhz|khz)?/gi, "") // Remove frequency
      .replace(/stockholm|göteborg|malmö|skåne|national/gi, "") // Remove city/region
      .replace(/\s+/g, " ")
      .trim();

  // Extract core brand name (first 2-3 significant words)
  const extractBrand = (name: string) => {
    const normalized = name
      .toLowerCase()
      .replace(/[0-9.,]+/g, "")
      .replace(
        /\s*(fm|am|mhz|khz|radio|stockholm|göteborg|malmö|skåne|national)\s*/gi,
        " ",
      )
      .trim();
    return normalized.split(/\s+/).slice(0, 3).join(" ");
  };

  const staticMatch = ALL_STATIC_STATIONS.find((s) => {
    // 1. Exact ID match (highest priority, no country check needed)
    if (s.id === station.id) return true;

    const staticName = normalizeName(s.name);
    const stationName = normalizeName(station.name);

    // 2. Exact name match (works cross-country for international stations like BBC, KEXP)
    if (staticName === stationName && staticName.length >= 3) return true;

    // 3. For same-country matching, use more flexible rules
    const sameCountry = s.countryCode === station.countryCode;

    // Brand-based matching (e.g., "Mix Megapol" matches "Mix Megapol 104,3 Stockholm")
    const staticBrand = extractBrand(s.name);
    const stationBrand = extractBrand(station.name);
    if (staticBrand && stationBrand && staticBrand.length >= 4) {
      if (staticBrand === stationBrand) return true;
      // One brand contains the other (only for same country to avoid false positives)
      if (
        sameCountry &&
        (staticBrand.includes(stationBrand) ||
          stationBrand.includes(staticBrand))
      ) {
        const minLen = Math.min(staticBrand.length, stationBrand.length);
        if (minLen >= 4) return true;
      }
    }

    // 4. Partial name match (e.g., "KEXP" vs "KEXP 90.3 FM")
    if (staticName.includes(stationName) || stationName.includes(staticName)) {
      const minLength = Math.min(staticName.length, stationName.length);
      if (minLength >= 4) return true;
    }

    return false;
  });

  if (staticMatch) {
    // Force the curated logo if available (Fixes broken/blocked remote logos)
    if (staticMatch.logoUrl) {
      station.logoUrl = staticMatch.logoUrl;
    }
    // Enhance other metadata
    if (!station.color && staticMatch.color) station.color = staticMatch.color;
    if (!station.website && staticMatch.website)
      station.website = staticMatch.website;
  }

  // 2. Try to match with an Operator (Provider) for colors and metadata
  const operator = operators.find((op) =>
    op.matchPatterns.some(
      (pattern) =>
        station.streamUrl.includes(pattern) ||
        (station.website && station.website.includes(pattern)) ||
        station.id === op.id,
    ),
  );

  if (operator) {
    if (operator.color && !station.color) {
      station.color = operator.color;
    }
  }

  return station;
};

// --- Aggregator Logic ---

/**
 * Primary function to get stations based on criteria.
 * It intelligently mixes "Gold Providers" (SR, NRK etc) with the global Radio Browser database.
 */
/**
 * Searches for radio stations across multiple providers (Supabase, Radio Browser).
 * Handles smart merging, deduplication, and fallback logic for logos.
 *
 * @param params Search criteria including name, country, tag/genre, etc.
 * @returns A promise resolving to a list of enriched stations
 */
export const searchStations = async (params: {
  name?: string;
  country?: string; // Full name or "All"
  tag?: string;
  limit?: number;
  hasLogo?: boolean;
  sortBy?: "relevance" | "name" | "popular";
}): Promise<Station[]> => {
  const { name, country, limit = 50, sortBy = "relevance" } = params;
  let stations: Station[] = [];

  // ... (rest of function) ...

  // 1. Determine strategy based on input
  const isSearchByName = !!name;
  const countryName = country;

  // Helper to map full country name to code (simplified)
  const getCountryCode = (name?: string) => {
    if (!name || name === "All") return undefined;
    const n = name.toLowerCase();
    if (n === "sweden") return "SE";
    if (n === "norway") return "NO";
    if (n === "portugal") return "PT";
    if (n === "australia") return "AU";
    if (n === "new zealand") return "NZ";
    return undefined;
  };

  const countryCode = getCountryCode(countryName);

  // 2. Fetch from Gold Providers if applicable
  if (!isSearchByName) {
    // --- SUPABASE (Replaces static files) ---
    let sbQuery = supabase.from("stations").select("*");

    // Optimistic filtering for Supabase to reduce payload
    if (countryName && countryName !== "All") {
      sbQuery = sbQuery.ilike("country", countryName);
    }

    const { data: sbData } = await sbQuery;

    if (sbData) {
      let filteredSbData = sbData;

      // In-memory tag filtering for Supabase results
      if (params.tag) {
        const searchTags = params.tag
          .toLowerCase()
          .split(",")
          .map((t) => t.trim());
        filteredSbData = sbData.filter((s: SupabaseStationRow) => {
          if (!s.tags || !Array.isArray(s.tags)) return false;
          // Check if station has ANY of the requested tags
          return s.tags.some((t: string) =>
            searchTags.some((st) => t.toLowerCase().includes(st)),
          );
        });
      }

      // Filter by logo presence if requested
      if (params.hasLogo) {
        filteredSbData = filteredSbData.filter(
          (s: SupabaseStationRow) => s.logo_url && s.logo_url.length > 0,
        );
      }

      const mappedSb: Station[] = filteredSbData.map(mapSupabaseStation);
      stations = [...stations, ...mappedSb];
    }

    // --- SR API (Keep for full P4 coverage) ---
    if (countryName === "Sweden" || countryCode === "SE") {
      const sr = await getSverigesRadioStations();
      // Avoid duplicates (Supabase takes precedence for "curated" ones like P1/P2/P3 if they exist there)
      // But actually, SR API might have better metadata.
      // Let's deduplicate by ID.
      const existingIds = new Set(stations.map((s) => s.id));
      sr.forEach((s) => {
        if (!existingIds.has(s.id)) {
          s.stationType = "mainstream";
          stations.push(s);
        }
      });
    }
  }

  // 3. Fetch from Radio Browser (Global Fallback)
  const rbLimit = stations.length > 0 ? 20 : limit;

  // Smart Tag Expansion: Try the new genre mapping first, then fall back to old mappings
  let expandedTag: string | undefined = undefined;
  if (params.tag) {
    // First, try the new genre tag mapping
    const genreMapping = mapGenreToMetaFilters(params.tag);
    if (genreMapping && genreMapping.tags.length > 0) {
      // Use the first tag as primary, or join multiple tags with comma
      expandedTag = genreMapping.tags[0];
      // console.log(`[RadioService] Genre "${params.tag}" mapped to tag: "${expandedTag}"`);
    } else {
      // Fall back to old TAG_EXPANSIONS
      expandedTag = TAG_EXPANSIONS[params.tag] || params.tag;
    }
  }

  const rbStations = await getRbStations({
    name: params.name,
    tag: expandedTag,
    countryCode: countryCode,
    country: !countryCode && countryName !== "All" ? countryName : undefined,
    limit: rbLimit,
  });

  // 4. SMART Merge and Deduplicate with Logo Patching
  const normalizeName = (name: string) =>
    name
      .toLowerCase()
      .replace(/\s/g, "")
      .replace(/^sverigesradio/, "")
      .replace(/^sr/, "")
      .replace(/-/g, "");

  // Create lookup map for gold provider stations by normalized name
  const goldMap = new Map<string, Station>();
  stations.forEach((s) => {
    const key = normalizeName(s.name);
    goldMap.set(key, s);
  });

  // Add Radio Browser stations, but patch with gold provider metadata if available
  rbStations.forEach((rb) => {
    const normalizedName = normalizeName(rb.name);
    const goldStation = goldMap.get(normalizedName);

    if (goldStation) {
      // Duplicate detected: patch RB station with reliable gold provider logo
      // This fixes broken Wikimedia URLs from Radio Browser
      if (
        goldStation.logoUrl &&
        (!rb.logoUrl || rb.logoUrl.includes("wikimedia"))
      ) {
        rb.logoUrl = goldStation.logoUrl;
      }
      // Also patch other metadata if missing/better
      if (goldStation.streamUrl) rb.streamUrl = goldStation.streamUrl;
      if (goldStation.website) rb.website = goldStation.website;
      if (goldStation.bitrate && goldStation.bitrate > (rb.bitrate || 0))
        rb.bitrate = goldStation.bitrate;
    } else {
      // No duplicate: add this RB station
      stations.push(rb);
      goldMap.set(normalizedName, rb);
    }
  });

  // 5. STRICT FILTERING
  // CRITICAL FIX: Apply strict filtering even if searching by name, if a country was specified.
  if (countryName && countryName !== "All") {
    stations = stations.filter((s) => {
      // If provider is NOT radio-browser, we trust it (it came from Gold Provider logic above)
      // But wait, if we search by name, we might get global results from RB.
      // So we should filter EVERYTHING if country is specified.

      const sCountry = (s.country || "").toLowerCase();
      const tCountry = countryName.toLowerCase();

      // Allow if country matches
      if (sCountry.includes(tCountry) || tCountry.includes(sCountry))
        return true;

      // Also check country code if available
      if (s.countryCode && countryCode && s.countryCode === countryCode)
        return true;

      return false;
    });
  }

  // 6. AGGRESSIVE DEDUPLICATION (The "Cleaner")
  stations = deduplicateStations(stations);

  // 7. Sorting
  stations.sort((a, b) => {
    // NEW: Alphabetical Sort
    if (sortBy === "name") {
      return a.name.localeCompare(b.name);
    }

    // NEW: Popularity Sort (Votes)
    if (sortBy === "popular") {
      return (b.votes || 0) - (a.votes || 0);
    }

    // 0. Search Relevance (if searching by name)
    if (isSearchByName && params.name) {
      // ... (relevance logic) ...
    }

    const aGold = a.provider !== "radio-browser" ? 1 : 0;
    const bGold = b.provider !== "radio-browser" ? 1 : 0;
    if (aGold !== bGold) return bGold - aGold;
    return (b.votes || 0) - (a.votes || 0);
  });

  // 8. Filter out blocked countries
  stations = stations.filter((s) => !isBlockedCountry(s));

  return stations.slice(0, limit).map(enrichStationData);
};

// --- Deduplication Logic ---

const normalizeForDeduplication = (name: string): string => {
  let n = name.toLowerCase();

  // 1. Remove "Sveriges Radio" / "SR" prefix explicitly first
  n = n.replace(/sveriges\s?radio/gi, "").replace(/\bsr\b/gi, "");

  // 2. Remove frequencies (Robust)
  // Match 2-3 digits, optional decimal/comma + digit.
  // Use whitespace boundaries instead of \b to be safe with unicode/punctuation
  n = n.replace(/(?:^|\s)(10[0-9]|[89][0-9])([.,][0-9])?(?=$|\s)/g, "");

  // 3. Remove common suffixes
  n = n.replace(
    /(?:^|\s)(fm|hd|hq|opus|aac|mp3|radio|live|on\s?air|web|net|digital|station|kanal)(?=$|\s)/gi,
    "",
  );

  // 4. Remove Cities (Expanded & Unicode safe)
  const cities =
    "stockholm|göteborg|malmö|skåne|riks|sweden|sverige|gbg|västra|norrbotten|uppland|malmöhus";
  n = n.replace(new RegExp(`(?:^|\\s)(${cities})(?=$|\\s)`, "gi"), "");

  // 5. Wildcard P4/P3 grouping
  // If it contains P4 or P3, strip everything after it?
  // "P4 Stockholm" -> "P4"
  // "P4" -> "P4"
  n = n.replace(/(p[34])\s+.*/gi, "$1");

  // 6. Clean up
  return n.replace(/[^a-z0-9]/g, "").trim();
};

const deduplicateStations = (stations: Station[]): Station[] => {
  const groups = new Map<string, Station[]>();

  stations.forEach((s) => {
    // Special case: Don't deduplicate SR stations too aggressively if we want to keep some regionality?
    // User said: "vi kan nog leva utan dom". So we treat them same.

    let key = normalizeForDeduplication(s.name);

    // Fallback for very short names or empty results
    if (!key || key.length < 2) {
      key = s.name.toLowerCase().replace(/[^a-z0-9]/g, "");
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(s);
  });

  const winners: Station[] = [];

  groups.forEach((group) => {
    if (group.length === 1) {
      winners.push(group[0]);
      return;
    }

    // Sort to find the winner
    group.sort((a, b) => {
      // 1. Prefer "Gold" providers (SR, etc) over Radio Browser
      const aGold = a.provider !== "radio-browser";
      const bGold = b.provider !== "radio-browser";
      if (aGold && !bGold) return -1;
      if (!aGold && bGold) return 1;

      // 2. Prefer Shortest Name (cleanest brand, e.g. "Mix Megapol" vs "Mix Megapol Göteborg")
      if (a.name.length !== b.name.length) {
        return a.name.length - b.name.length;
      }

      // 3. Prefer Higher Bitrate
      const aBit = a.bitrate || 0;
      const bBit = b.bitrate || 0;
      if (aBit !== bBit) return bBit - aBit;

      // 4. Prefer Most Votes
      return (b.votes || 0) - (a.votes || 0);
    });

    winners.push(group[0]);
  });

  return winners;
};

/**
 * Retrieves a single station by its unique ID.
 * First checks Supabase (curated/saved stations), then falls back to Radio Browser.
 *
 * @param id The station ID
 * @returns The station object or null if not found
 */
export const getStationById = async (id: string): Promise<Station | null> => {
  // 1. Try Supabase
  if (supabase) {
    const { data } = await supabase
      .from("stations")
      .select("*")
      .eq("id", id)
      .single();
    if (data) {
      const station = mapSupabaseStation(data);
      // Block stations from restricted countries
      if (isBlockedCountry(station)) return null;
      return station;
    }
  }

  // 2. Try Radio Browser
  const rbStation = await getRbStationById(id);
  if (rbStation) {
    const enriched = enrichStationData(rbStation);
    // Block stations from restricted countries
    if (isBlockedCountry(enriched)) return null;
    return enriched;
  }
  return null;
};

/**
 * Advanced Related Stations Logic
 * Prioritizes: Genre/Tags > Country > Bitrate > Votes
 */
/**
 * Advanced Related Stations Logic
 * Prioritizes: Genre/Tags > Country > Bitrate > Votes
 *
 * @param station The reference station
 * @returns Top matching related stations
 */
export const getRelatedStations = async (
  station: Station,
): Promise<Station[]> => {
  try {
    // 1. Determine search query (Name prefix or Tag)
    const nameParts = station.name.split(" ");
    let query = "";
    let searchType: "name" | "tag" = "name";

    if (nameParts.length > 1) {
      if (station.name.toLowerCase().includes("sveriges radio"))
        query = "Sveriges Radio";
      else if (station.name.toLowerCase().includes("bbc")) query = "BBC";
      else query = nameParts.slice(0, 2).join(" ");
    } else {
      query = station.tags[0] || "";
      searchType = "tag";
    }

    // 2. Fetch candidates
    let candidates = await searchStations({
      name: searchType === "name" ? query : undefined,
      tag: searchType === "tag" ? query : undefined,
      country: station.country, // Prefer same country initially
      limit: 30, // Fetch more to allow for filtering and sorting
    });

    // Fallback: If few results, widen search to just tags
    if (candidates.length < 5 && station.tags.length > 0) {
      const tagCandidates = await searchStations({
        tag: station.tags[0],
        limit: 20,
      });
      candidates = [...candidates, ...tagCandidates];
    }

    // 3. Filter & Deduplicate
    // Use robust deduplication to merge similar stations (handling multi-ID duplicates)
    let uniqueCandidates = deduplicateStations(candidates);

    // Explicitly remove the current station (by ID and by normalized name)
    const currentNormalized = normalizeForDeduplication(station.name);
    uniqueCandidates = uniqueCandidates.filter(
      (s) =>
        s.id !== station.id &&
        normalizeForDeduplication(s.name) !== currentNormalized,
    );

    // 4. Scoring & Sorting
    const scored = uniqueCandidates.map((s) => {
      let score = 0;

      // Criteria A: Tag Match (Heavy weight)
      const sharedTags = s.tags.filter((t) => station.tags.includes(t)).length;
      score += sharedTags * 10;

      // Criteria B: Same Country (Medium weight)
      if (s.country === station.country) score += 5;

      // Criteria C: High Bitrate (Tie-breaker)
      if (s.bitrate >= 192) score += 2;
      else if (s.bitrate >= 128) score += 1;

      // Criteria D: Popularity (Tie-breaker)
      score += Math.min((s.votes || 0) / 1000, 2); // Cap vote influence

      return { station: s, score };
    });

    scored.sort((a, b) => b.score - a.score);

    return scored.map((item) => item.station).slice(0, 6); // Return top 6
  } catch (e) {
    console.error("Failed to get related stations", e);
    return [];
  }
};

// --- Statistics for Dynamic Filtering ---

export const getGenreCountsForCountry = async (
  countryName: string,
): Promise<Record<string, number>> => {
  if (!countryName || countryName === "All") return {};

  const stations = await getRbStationsForStats(countryName);
  const counts: Record<string, number> = {};

  stations.forEach((station) => {
    station.tags.forEach((tag) => {
      const normalizedTag = tag.toLowerCase().trim();
      if (normalizedTag.length > 2) {
        counts[normalizedTag] = (counts[normalizedTag] || 0) + 1;
      }
    });
  });

  return counts;
};

// --- HOME CAROUSEL LOGIC ---

// streamHomeRows and hardcoded categories removed in favor of DB-driven getHomeData

// --- Legacy Wrappers ---

export const getTopStations = async (): Promise<Station[]> => {
  try {
    // Fetch top stations from Supabase
    let data;
    if (supabase) {
      const response = await supabase
        .from("stations")
        .select("*")
        .order("votes", { ascending: false })
        .limit(5);
      data = response.data;
    }

    // Import static stations for fallback data
    const { NRK_STATIONS, RTP_STATIONS } =
      await import("./data/staticStations");
    const allStatic = [...NRK_STATIONS, ...RTP_STATIONS];

    let sbStations: Station[] = [];
    if (data) {
      sbStations = data.map((s: SupabaseStationRow) => {
        // Find matching static station to fill in gaps (like logoUrl)
        const staticMatch = allStatic.find((staticS) => staticS.id === s.id);

        return {
          id: s.id,
          name: s.name,
          country: s.country,
          countryCode: s.country_code,
          state: s.state,
          provider: s.provider as StationSource,
          streamUrl: s.stream_url,
          website: s.website,
          // Prefer static local path (verified high quality) over Supabase URL
          // This ensures the fallback mechanism works (requires relative path)
          logoUrl: staticMatch?.logoUrl || s.logo_url,
          bitrate: s.bitrate,
          codec: s.codec,
          language: s.language && typeof s.language === 'string'
            ? s.language.split(",").map((l) => l.trim())
            : Array.isArray(s.language) ? s.language : [],
          tags: s.tags,
          votes: s.votes,
          clickTrend: s.click_trend,
          onlineStatus: s.online_status ? "online" : "offline",
        };
      });
    }

    const rb = await getRbStations({ limit: 20 });

    // Filter out blocked countries
    const allStations = [...sbStations, ...rb]
      .filter((s) => !isBlockedCountry(s))
      .slice(0, 30)
      .map(enrichStationData);

    return allStations;
  } catch (e) {
    console.error("Failed to get top stations", e);
    return [];
  }
};

export const getStationsByLocation = async (
  lat: number,
  long: number,
): Promise<Station[]> => {
  const stations = await getRbStationsByGeo(lat, long);
  return stations.map(enrichStationData).filter((s) => !isBlockedCountry(s));
};

export const getCountries = async (): Promise<
  { name: string; stationcount: number; iso_3166_1?: string }[]
> => {
  let data: { name: string; stationcount: number; iso_3166_1?: string }[] = [];
  try {
    data = await getRbCountries();
  } catch (error) {
    console.warn(
      "Radio Browser API failed, falling back to core countries:",
      error,
    );
  }

  try {
    // Ensure these core markets are ALWAYS present for Podcast support
    const CORE_PODCAST_COUNTRIES = [
      "Argentina",
      "Australia",
      "Austria",
      "Belgium",
      "Brazil",
      "Bulgaria",
      "Canada",
      "Chile",
      "China",
      "Colombia",
      "Czech Republic",
      "Denmark",
      "Egypt",
      "Finland",
      "France",
      "Germany",
      "Greece",
      "Hungary",
      "Iceland",
      "India",
      "Indonesia",
      "Ireland",
      "Israel",
      "Italy",
      "Japan",
      "Mexico",
      "Netherlands",
      "New Zealand",
      "Nigeria",
      "Norway",
      "Peru",
      "Poland",
      "Portugal",
      "Romania",
      "Russia",
      "Saudi Arabia",
      "South Africa",
      "South Korea",
      "Spain",
      "Sweden",
      "Switzerland",
      "Thailand",
      "Turkey",
      "Ukraine",
      "United Kingdom",
      "United States",
      "Venezuela",
      "Vietnam",
    ];

    // Create a map of existing countries from API
    const countryMap = new Map();
    data.forEach((c) => countryMap.set(c.name, c));

    // Add core countries if missing or force them to exist with at least 1 count (visual hack)
    CORE_PODCAST_COUNTRIES.forEach((coreCountry) => {
      if (countryMap.has(coreCountry)) {
        // If it exists but has 0 stations, bump it up so it shows in the filter
        const existing = countryMap.get(coreCountry);
        if (existing.stationcount < 1) {
          existing.stationcount = 100;
          countryMap.set(coreCountry, existing);
        }
      } else {
        countryMap.set(coreCountry, {
          name: coreCountry,
          stationcount: 100,
          iso_3166_1: "XX",
        });
      }
    });

    // Convert map back to array
    const mergedCountries = Array.from(countryMap.values());
    console.log(
      "[radioService] Merged countries count:",
      mergedCountries.length,
    );
    console.log(
      "[radioService] Checking Argentina:",
      mergedCountries.find((c) => c.name === "Argentina"),
    );

    return mergedCountries
      .filter((c) => c && c.stationcount >= 1)
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error("Error processing countries:", error);
    return [];
  }
};

/**
 * Fetches the main dashboard data for a given country or worldwide view.
 * Utilizes a caching layer (memory/localstorage) for performance.
 * Aggregates carousels, top lists, and recommended content.
 *
 * @param country The target country (or 'Worldwide')
 * @param user Current user session (for personalized content)
 * @param options Options to bypass cache
 * @returns Object containing hero, globalTop, local, recommended, and rows data
 */
export const getHomeData = async (
  country: string,
  user?: { id: string },
  options?: { skipCache?: boolean },
) => {
  const normalizedCountry = country === "Worldwide" ? "All" : country;
  const now = Date.now();

  // Import new cache system
  const { getFromCacheWithStale, setInCache, CACHE_KEYS, TTL } =
    await import("../lib/cache");
  const cacheKey = CACHE_KEYS.HOME_RADIO(normalizedCountry);

  // Check new cache system first (unless skipCache is true)
  if (!options?.skipCache) {
    const cached = getFromCacheWithStale<{
      hero: Station[];
      globalTop: Station[];
      local: Station[];
      recommended: Station[];
      rows: HomeRow[];
    }>(cacheKey);

    if (cached && !cached.isStale) {
      // console.log('[radioService] Returning fresh cached data for:', normalizedCountry);
      return cached.data;
    }

    if (cached && cached.isStale) {
      // console.log('[radioService] Returning stale data, revalidating in background...');
      // Trigger background revalidation
      setTimeout(() => {
        getHomeData(country, user, { skipCache: true })
          .then((freshData) => {
            setInCache(cacheKey, freshData, TTL.HOME_DATA);
          })
          .catch((err) =>
            console.warn("[radioService] Background revalidation failed:", err),
          );
      }, 100);
      return cached.data;
    }
  }

  // Legacy cache fallback (for backwards compatibility during migration)
  const legacyCacheKey = `${CACHE_KEY_PREFIX}home_data_${normalizedCountry}_${user?.id || "anon"}`;
  try {
    const cached = localStorage.getItem(legacyCacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (now - parsed.timestamp < CACHE_DURATION) {
        // Migrate to new cache
        setInCache(cacheKey, parsed.data, TTL.HOME_DATA);
        return parsed.data;
      }
    }
  } catch (e) {
    console.warn("Failed to load legacy cache", e);
  }

  // Fetch fresh data from database carousels
  try {
    // Import services dynamically to avoid circular dependencies
    const { fetchGlobalCarouselsForCountry } =
      await import("./globalCarouselService");
    const { executeCarouselFilter } = await import("./carouselQueryService");

    // Fetch enabled carousels for this country (WITHOUT USER ID for caching)
    const carousels = await fetchGlobalCarouselsForCountry(
      normalizedCountry,
      "radio",
    );
    const enabled = carousels.filter((c) => c.enabled);

    // Separate carousels by position
    const topCarousels = enabled.filter((c) => c.position === "top");
    const bottomCarousels = enabled.filter((c) => c.position === "bottom");
    const indexedCarousels = enabled.filter((c) =>
      c.position?.startsWith("index:"),
    );

    // Sort indexed carousels by their index number
    indexedCarousels.sort((a, b) => {
      const indexA = parseInt(a.position.split(":")[1] || "0");
      const indexB = parseInt(b.position.split(":")[1] || "0");
      return indexA - indexB;
    });

    // Combine in order: top → indexed → bottom
    const orderedCarousels = [
      ...topCarousels,
      ...indexedCarousels,
      ...bottomCarousels,
    ];

    // Execute filter_rules and fetch stations for each carousel
    const rows = [];
    for (const carousel of orderedCarousels) {
      try {
        const effectiveRules = {
          ...(carousel.filter_rules || {}),
          type: carousel.filter_rules?.type || carousel.type,
        } as unknown as FilterRules;

        const requiresAuth =
          effectiveRules?.requireAuth || effectiveRules.type === "personalized";
        if (requiresAuth && !user) {
          continue; // Skip personalized rails for logged-out users
        }

        // Fix: user should be passed directly, fallbackType was unused/hacky
        const stations = await executeCarouselFilter(
          effectiveRules as FilterRules,
          user,
        );

        rows.push({
          id: carousel.id,
          title: carousel.title,
          subtitle: carousel.subtitle || "",
          stations: stations || [],
          type: (effectiveRules.type ||
            carousel.type ||
            "curated") as HomeRow["type"],
          filterTag: effectiveRules?.tag,
        });
      } catch (error) {
        console.error(`[radioService] Error loading carousel:`, error);
      }
    }

    // --- DYNAMIC FALLBACK ROWS FOR COUNTRIES ---
    // If we are in a specific country view, ensure we have at least some basic content
    // This fixes the "No carousels found" issue for countries without curated lists
    if (normalizedCountry !== "All") {
      const existingRowIds = new Set(rows.map((r) => r.id));

      // Track station IDs already used in any carousel to avoid duplicates
      const usedStationIds = new Set<string>();
      rows.forEach((r) => r.stations?.forEach((s) => usedStationIds.add(s.id)));

      // Helper to filter out already-used stations
      const filterUnique = (stations: Station[]): Station[] => {
        return stations.filter((s) => {
          if (usedStationIds.has(s.id)) return false;
          usedStationIds.add(s.id); // Mark as used
          return true;
        });
      };

      // 1. Most Popular in [Country] - All genres, sorted by votes
      if (!existingRowIds.has(`popular_${normalizedCountry}`)) {
        let popularStations = await searchStations({
          country: normalizedCountry,
          sortBy: "popular",
          limit: 30,
        });

        // FALLBACK
        if (popularStations.length < 5) {
          const globalPopular = await searchStations({
            country: "All",
            sortBy: "popular",
            limit: 15,
          });
          const existingIds = new Set(popularStations.map((s) => s.id));
          const uniqueGlobal = globalPopular.filter(
            (s) => !existingIds.has(s.id),
          );
          popularStations = [...popularStations, ...uniqueGlobal];
        }

        const uniquePopular = filterUnique(popularStations).slice(0, 12);
        if (uniquePopular.length > 0) {
          rows.push({
            id: `popular_${normalizedCountry}`,
            title: `Most Popular in ${normalizedCountry}`,
            subtitle: "Top rated stations",
            stations: uniquePopular,
            type: "curated",
            filterTag: "popular",
          });
        }
      }

      // 2. Rock & Alternative
      if (!existingRowIds.has(`rock_${normalizedCountry}`)) {
        let rockStations = await searchStations({
          country: normalizedCountry,
          tag: "rock",
          limit: 30,
        });

        // FALLBACK
        if (rockStations.length < 5) {
          const globalRock = await searchStations({
            country: "All",
            tag: "rock",
            limit: 15,
          });
          const existingIds = new Set(rockStations.map((s) => s.id));
          const uniqueGlobal = globalRock.filter((s) => !existingIds.has(s.id));
          rockStations = [...rockStations, ...uniqueGlobal];
        }

        const uniqueRock = filterUnique(rockStations).slice(0, 12);
        if (uniqueRock.length >= 3) {
          rows.push({
            id: `rock_${normalizedCountry}`,
            title: `Rock & Alternative in ${normalizedCountry}`,
            subtitle: "Turn it up",
            stations: uniqueRock,
            type: "curated",
            filterTag: "rock",
          });
        }
      }

      // 3. Classics & Oldies
      if (!existingRowIds.has(`classic_${normalizedCountry}`)) {
        let classicStations = await searchStations({
          country: normalizedCountry,
          tag: "oldies",
          limit: 30,
        });

        // FALLBACK
        if (classicStations.length < 5) {
          const globalClassic = await searchStations({
            country: "All",
            tag: "oldies",
            limit: 15,
          });
          const existingIds = new Set(classicStations.map((s) => s.id));
          const uniqueGlobal = globalClassic.filter(
            (s) => !existingIds.has(s.id),
          );
          classicStations = [...classicStations, ...uniqueGlobal];
        }

        const uniqueClassic = filterUnique(classicStations).slice(0, 12);
        if (uniqueClassic.length >= 3) {
          rows.push({
            id: `classic_${normalizedCountry}`,
            title: `Classics & Oldies in ${normalizedCountry}`,
            subtitle: "Timeless hits",
            stations: uniqueClassic,
            type: "curated",
            filterTag: "oldies",
          });
        }
      }

      // 4. Dance & Electronic
      if (!existingRowIds.has(`dance_${normalizedCountry}`)) {
        let danceStations = await searchStations({
          country: normalizedCountry,
          tag: "dance",
          limit: 30,
        });

        // FALLBACK
        if (danceStations.length < 5) {
          const globalDance = await searchStations({
            country: "All",
            tag: "dance",
            limit: 15,
          });
          const existingIds = new Set(danceStations.map((s) => s.id));
          const uniqueGlobal = globalDance.filter(
            (s) => !existingIds.has(s.id),
          );
          danceStations = [...danceStations, ...uniqueGlobal];
        }

        const uniqueDance = filterUnique(danceStations).slice(0, 12);
        if (uniqueDance.length >= 3) {
          rows.push({
            id: `dance_${normalizedCountry}`,
            title: `Dance & Electronic in ${normalizedCountry}`,
            subtitle: "Feel the beat",
            stations: uniqueDance,
            type: "curated",
            filterTag: "dance",
          });
        }
      }
    }

    // Filter out rows with no stations
    const validRows = rows.filter((r) => r.stations && r.stations.length > 0);

    // Map to existing structure for compatibility with HomeView
    // Prefer a non-stats/non-personalized row as hero; fall back to first
    const heroRow =
      validRows.find((r) => r.type !== "stats" && r.type !== "personalized") ||
      validRows[0];
    const heroStations = heroRow?.stations || [];

    // Use remaining rows for the list (excluding the one used for hero)
    const displayRows = validRows.filter((r) => r.id !== heroRow?.id);

    const localRow = validRows.find((r) =>
      r.id.toLowerCase().includes("local"),
    );
    const globalRow = validRows.find(
      (r) =>
        r.id.toLowerCase().includes("global") ||
        r.id.toLowerCase().includes("top"),
    );
    const recommendedRow = validRows.find((r) =>
      r.id.toLowerCase().includes("recommend"),
    );

    const data = {
      hero: heroStations,
      globalTop: globalRow?.stations || [],
      local: localRow?.stations || [],
      recommended: recommendedRow?.stations || [],
      rows: displayRows, // Return only the rows that should be displayed in the list
    };

    // Save to new cache system
    setInCache(cacheKey, data, TTL.HOME_DATA);

    // Also save to legacy cache for backwards compatibility
    try {
      localStorage.setItem(
        legacyCacheKey,
        JSON.stringify({
          timestamp: now,
          data,
        }),
      );
    } catch (e) {
      console.warn("Failed to save legacy cache", e);
    }

    return data;
  } catch (error) {
    console.error("Error fetching home data from carousels", error);

    // Fallback to empty data structure
    return {
      hero: [],
      globalTop: [],
      local: [],
      recommended: [],
      rows: [],
    };
  }
};

/**
 * Filter rows based on user preferences.
 * This should be applied AFTER fetching cached global data.
 */
const applyUserPreferencesSync = (
  rows: HomeRow[],
  prefs: UserHomePreference[],
): HomeRow[] => {
  if (!prefs || prefs.length === 0) return rows;

  const hiddenSectionIds = new Set(
    prefs.filter((p) => !p.isVisible).map((p) => p.sectionId),
  );

  return rows.filter((row) => {
    // Row IDs are prefixed with "global_" but legacy ones might match directly?
    // Check both ID and ID without "global_" prefix
    const rawId = row.id.startsWith("global_")
      ? row.id.replace("global_", "")
      : row.id;

    if (hiddenSectionIds.has(rawId) || hiddenSectionIds.has(row.id)) {
      return false;
    }
    return true;
  });
};

/**
 * Wrapper for getHomeData that applies user personalization.
 * This is what the UI should call.
 */
export const getPersonalizedHomeData = async (
  country: string,
  user?: { id: string },
  options?: { skipCache?: boolean },
) => {
  // 1. Get Global Data (Cached)
  const data = await getHomeData(country, undefined, options);

  // 2. Apply User Personalization (In-Memory)
  if (user) {
    try {
      const prefs = await getUserHomePreferences(user.id);
      if (prefs && prefs.length > 0) {
        // Deep copy data to avoid mutating cache
        const personalizedData = { ...data };
        personalizedData.rows = applyUserPreferencesSync(
          personalizedData.rows,
          prefs,
        );
        return personalizedData;
      }
    } catch (e) {
      console.warn("[radioService] Failed to load user prefs for home data", e);
    }
  }

  return data;
};
